import type { CreateAgentRequest, RosterAgent, SidebarSettings } from "@ark-bot/protocol";
import { useEffect, useRef, useState } from "react";

import type { ConnectionStatus } from "../api.js";
import { configuredHost } from "../host.js";
import { createPortal } from "react-dom";

import { useBackClose, useMedia } from "../back.js";
import { useFlip } from "../flip.js";
import { useText } from "../i18n.js";
import { usePresence } from "../presence.js";
import { activityList, loadFolded, pinStep, shortcutLabel, toggleFolded } from "../navigation.js";
import { isUnread, matchesQuery, orderRoster, previewOf, relativeTime } from "../roster.js";
import { LONG_PRESS_MS, TOUCH_QUERY, menuOpensFrom, wandered } from "../row-gesture.js";
import { arrange, sectionOf } from "../sidebar-settings.js";
import { SIDEBAR } from "../text/sidebar.js";
import { displayDateTime } from "../time-zone.js";

import { AccountFoot } from "./AccountMenu.js";
import { ActiveGlide } from "./ActiveGlide.js";
import { Avatar } from "./Avatar.js";
import { BrandMark } from "./BrandMark.js";
import { NameDialog, type NameRequest } from "./NameDialog.js";
import { NewChat } from "./NewChat.js";
import { ContextMenu, type Divider, type MenuItem, type MenuState } from "./ContextMenu.js";
import { MenuIcon } from "./MenuIcon.js";

/** Everything the roster can be asked to do to one agent or one section; the app owns the settings and does it. */
export interface RosterActions {
  readonly onSelect: (id: string) => void;
  /** One more agent, or with `kind: "group"` and `members`, a room. */
  readonly onCreate: (request: CreateAgentRequest) => Promise<void>;
  readonly onDelete: (agent: RosterAgent) => Promise<void>;
  readonly onDuplicate: (agent: RosterAgent) => Promise<void>;
  readonly onEditProfile: (id: string) => void;
  readonly onMarkUnread: (id: string) => void;
  readonly onTogglePinned: (id: string) => void;
  readonly onReorderPinned: (id: string, targetId: string, position: "before" | "after") => void;
  readonly onMoveToSection: (id: string, sectionId: string | null) => void;
  readonly onNewSection: (name: string, agentId?: string) => void;
  readonly onRenameSection: (id: string, name: string) => void;
  readonly onRemoveSection: (id: string) => void;
  readonly onReorderSection: (id: string, targetId: string, position: "before" | "after") => void;
  readonly onToggleHidden: (id: string) => void;
  /** Settings: the account, the time zone, the virtual machine and the permissions. */
  readonly onOpenSettings: () => void;
  /** The apps the user connects for their bots (§6.18). */
  readonly onOpenPlugins: () => void;
}

interface Props extends RosterActions {
  readonly agents: readonly RosterAgent[];
  readonly sidebar: SidebarSettings;
  readonly selectedId: string | null;
  /** When the user last had each chat open, by agent id; a newer message than that is unread. */
  readonly seen: Readonly<Record<string, number>>;
  /** Agents the user marked unread by hand. */
  readonly forcedUnread: ReadonlySet<string>;
  readonly now: number;
  readonly status: ConnectionStatus;
  /** Opens the connect screen; only the packaged app has a host to change. */
  readonly onOpenHost?: (() => void) | undefined;
  /** Puts the sidebar away on a wide screen; the chat's header brings it back. */
  readonly onCollapse?: (() => void) | undefined;
  /** Put away: out of sight, and out of reach of the keyboard and screen readers too. */
  readonly away?: boolean;
}

/** Where this device keeps which sections are folded: a view choice, not the host's arrangement. */
const FOLDED_KEY = "ark-bot.foldedSections";

function storedFolded(): Set<string> {
  try {
    return loadFolded(localStorage.getItem(FOLDED_KEY));
  } catch {
    return new Set();
  }
}

/** The face with, while the bot works, a ring turning round it; the face itself stays still. */
function Face({ agent, size, className, children }: { readonly agent: RosterAgent; readonly size: number; readonly className: string; readonly children?: React.ReactNode }): React.JSX.Element {
  return (
    <span className={`${className}${agent.busy ? " working" : ""}`}>
      <Avatar id={agent.id} name={agent.name} avatar={agent.config.avatar} size={size} className={className === "pin-face-wrap" ? "pin-face" : "agent-face"} />
      {agent.busy ? <span className="work-ring" aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

/** The configured host as the foot shows it: its name and port, without the scheme. */
function hostName(): string {
  const host = configuredHost();
  return host == null ? "" : new URL(host).host;
}

// --- the rows -----------------------------------------------------------------

interface RowProps {
  readonly agent: RosterAgent;
  readonly selected: boolean;
  readonly unread: boolean;
  readonly now: number;
  readonly members: string;
  readonly onSelect: () => void;
  readonly onMenu: (x: number, y: number) => void;
  /** A long press on a touch screen: the row is lifted to be carried, or, let go in place, opens its menu. */
  readonly onLift: (x: number, y: number) => void;
  readonly lifted: boolean;
  /** Whether the browser's own drag is offered: a mouse's, never a finger's (row-gesture.ts). */
  readonly htmlDrag: boolean;
  readonly onDragStart: () => void;
  readonly onDragEnd: () => void;
}

/**
 * Right click opens the row's menu. A long press on a touch screen lifts the
 * row instead, as a phone's home screen does: carried, it drops where it is
 * let go; let go where it is, the menu opens. The HTML drag below never
 * fires from a touch, so this is the phone's whole way to move a row — and
 * the `contextmenu` Android raises from that same press is left to the lift
 * rather than opening a second menu over the row being carried
 * (row-gesture.ts).
 */
function useRowPress(onMenu: (x: number, y: number) => void, onLift: (x: number, y: number) => void) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  /** What raised the press we are in; the finger's belongs to the lift alone. */
  const pointer = useRef<string | null>(null);
  const cancel = (): void => {
    if (timer.current != null) clearTimeout(timer.current);
    timer.current = null;
    start.current = null;
  };
  return {
    onPointerDown: (event: React.PointerEvent) => {
      pointer.current = event.pointerType;
    },
    onContextMenu: (event: React.MouseEvent) => {
      // Refused either way: the browser's own menu is never the one we show.
      event.preventDefault();
      if (menuOpensFrom(pointer.current)) onMenu(event.clientX, event.clientY);
    },
    onTouchStart: (event: React.TouchEvent) => {
      const touch = event.touches[0];
      if (touch == null || event.touches.length > 1) return;
      const { clientX, clientY } = touch;
      cancel();
      start.current = { x: clientX, y: clientY };
      timer.current = setTimeout(() => {
        timer.current = null;
        onLift(clientX, clientY);
      }, LONG_PRESS_MS);
    },
    onTouchMove: (event: React.TouchEvent) => {
      const touch = event.touches[0];
      const from = start.current;
      if (touch == null || from == null) return;
      if (wandered(from, { x: touch.clientX, y: touch.clientY })) cancel();
    },
    onTouchEnd: cancel,
    onTouchCancel: cancel,
  };
}

/**
 * One roster row, as the prototype draws it: the face, the name with its job
 * label beside it, when it last moved on the right, and under that what was
 * last said, or what it is waiting on. A dot on the face while it works.
 */
function Row({ agent, selected, unread, now, members, onSelect, onMenu, onLift, lifted, htmlDrag, onDragStart, onDragEnd }: RowProps): React.JSX.Element {
  const t = useText(SIDEBAR);
  const preview = previewOf(agent);
  const group = agent.kind === "group";
  const label = group ? members : (agent.config.title ?? "");
  const line = agent.busy && (preview == null || !preview.attention) ? t.working : (preview?.text ?? (group ? t.nobodySpoken : t.noMessages));
  const menu = useRowPress(onMenu, onLift);
  return (
    <li
      className={`agent-row${selected ? " selected" : ""}${unread ? " unread" : ""}${agent.busy ? " busy" : ""}${preview?.attention === true ? " attention" : ""}${lifted ? " lifted" : ""}`}
      data-agent-id={agent.id}
      draggable={htmlDrag}
      onDragStart={(event) => {
        event.dataTransfer.setData("text/plain", agent.id);
        event.dataTransfer.effectAllowed = "move";
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      {...menu}
    >
      <button type="button" className="agent-open" onClick={onSelect} aria-current={selected ? "page" : undefined} aria-label={agent.name} title={agent.config.description ?? agent.name}>
        <Face agent={agent} size={36} className="agent-face-wrap">
          {agent.busy ? <span className="visually-hidden">{t.workingDot}</span> : preview?.attention === true ? <span className="agent-dot attention" aria-label={t.needsYouDot} /> : null}
        </Face>
        <span className="agent-body">
          <span className="agent-line">
            <strong className="agent-title">
              {group ? <span className="agent-hash">#</span> : null}
              {agent.name}
            </strong>
            {label.trim().length > 0 ? <span className="agent-role tag">{label}</span> : null}
            <time className="agent-time" dateTime={new Date(agent.updatedAt).toISOString()} title={displayDateTime(agent.updatedAt)}>
              {relativeTime(agent.updatedAt, now)}
            </time>
          </span>
          <span className={`agent-preview${preview?.attention === true ? " attention" : ""}${agent.busy && preview?.attention !== true ? " working shimmer" : ""}`}>{line}</span>
        </span>
        {unread ? <span className="agent-unread" aria-label={t.unreadDot} /> : null}
      </button>
      <button type="button" className="agent-more" aria-label={t.optionsFor(agent.name)} title={t.options} onClick={(event) => onMenu(event.clientX, event.clientY)}>
        ···
      </button>
    </li>
  );
}

/** A pinned agent on the rail: the face large, the name, the job label; the prototype's pinned bot. */
function Pin({ agent, selected, unread, onSelect, onMenu, onLift, lifted, htmlDrag, touchOver, onDrop, onStep, onDragStart, onDragEnd }: { readonly agent: RosterAgent; readonly selected: boolean; readonly unread: boolean; readonly onSelect: () => void; readonly onMenu: (x: number, y: number) => void; readonly onLift: (x: number, y: number) => void; readonly lifted: boolean; readonly htmlDrag: boolean; readonly touchOver: "before" | "after" | null; readonly onDrop: (id: string, position: "before" | "after") => void; /** Alt+← or Alt+→: one place along the rail. */ readonly onStep: (direction: -1 | 1) => void; readonly onDragStart: () => void; readonly onDragEnd: () => void }): React.JSX.Element {
  const t = useText(SIDEBAR);
  const [over, setOver] = useState<"before" | "after" | null>(null);
  const menu = useRowPress(onMenu, onLift);
  const preview = previewOf(agent);
  const side = over ?? touchOver;
  return (
    <button
      type="button"
      className={`pin${selected ? " selected" : ""}${unread ? " unread" : ""}${side == null ? "" : ` over-${side}`}${lifted ? " lifted" : ""}`}
      data-pin-id={agent.id}
      onClick={onSelect}
      aria-current={selected ? "page" : undefined}
      aria-keyshortcuts="Alt+ArrowLeft Alt+ArrowRight"
      title={preview?.text ?? agent.name}
      onKeyDown={(event) => {
        if (!event.altKey || event.ctrlKey || event.metaKey || (event.key !== "ArrowLeft" && event.key !== "ArrowRight")) return;
        event.preventDefault();
        onStep(event.key === "ArrowLeft" ? -1 : 1);
      }}
      draggable={htmlDrag}
      onDragStart={(event) => {
        event.dataTransfer.setData("text/plain", agent.id);
        event.dataTransfer.effectAllowed = "move";
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onDragOver={(event) => {
        event.preventDefault();
        event.stopPropagation();
        const bounds = event.currentTarget.getBoundingClientRect();
        setOver(event.clientX < bounds.left + bounds.width / 2 ? "before" : "after");
      }}
      onDragLeave={() => setOver(null)}
      onDrop={(event) => {
        event.preventDefault();
        event.stopPropagation();
        const id = event.dataTransfer.getData("text/plain");
        const position = over ?? "after";
        setOver(null);
        if (id.length > 0 && id !== agent.id) onDrop(id, position);
      }}
      {...menu}
    >
      <Face agent={agent} size={52} className="pin-face-wrap">
        {agent.busy ? <span className="visually-hidden">{t.workingDot}</span> : preview?.attention === true ? <span className="agent-dot attention" aria-label={t.needsYouDot} /> : unread ? <span className="agent-dot unread" aria-label={t.unreadDot} /> : null}
      </Face>
      <span className="pin-name">{agent.name}</span>
      {agent.config.title != null && agent.config.title.trim().length > 0 ? <span className="agent-role tag">{agent.config.title}</span> : null}
    </button>
  );
}

/** What a dragged section carries, apart from a row's plain agent id, so a drop can tell the two apart. */
const SECTION_DRAG = "application/x-ark-section";

/** How a named section moves among the others. */
interface SectionMove {
  /** Its name can be dragged with a mouse; a finger lifts it with a long press instead, as a row. */
  readonly htmlDrag: boolean;
  /** A long press on its name: lifted, to be carried by the finger (or, let go in place, its menu). */
  readonly onLift: (x: number, y: number) => void;
  /** Where a section carried by a finger would land on this one. */
  readonly landing: "before" | "after" | null;
  /** The section being dragged, if one is. */
  readonly moving: string | null;
  readonly onStart: () => void;
  readonly onEnd: () => void;
  /** Another section, dropped on this one, lands before or after it. */
  readonly onDrop: (id: string, position: "before" | "after") => void;
  /** Alt+Up or Alt+Down on its name: one place along. */
  readonly onStep: (direction: -1 | 1) => void;
}

/**
 * A section's rows under its name; a row dragged onto it joins it. A named
 * section can itself be dragged by its name to another place among them, a
 * line showing where it will land. The unassigned block is the same with no
 * id, and stays last.
 */
function Section({ id, name, count, dragging, touchOver = false, folded = false, onFold, onDrop, onMenu, move, children }: { readonly id: string | null; readonly name: string | null; readonly count: number; readonly dragging: boolean; /** The lifted row is over this section. */ readonly touchOver?: boolean; /** Its rows are put away under its name; a drop still lands in it. */ readonly folded?: boolean; readonly onFold?: () => void; readonly onDrop: (agentId: string) => void; readonly onMenu?: (x: number, y: number) => void; readonly move?: SectionMove | undefined; readonly children: React.ReactNode }): React.JSX.Element {
  const t = useText(SIDEBAR);
  const [over, setOver] = useState(false);
  const [landing, setLanding] = useState<"before" | "after" | null>(null);
  const movable = id != null && move != null;
  // A named section's long press lifts it to be carried, as a row's does; the unassigned block's opens its menu.
  const menu = useRowPress(onMenu ?? (() => undefined), movable ? move.onLift : (onMenu ?? (() => undefined)));
  const shownLanding = landing ?? move?.landing ?? null;
  return (
    <section
      className={`agents-section${over || touchOver ? " drop-target" : ""}${dragging ? " droppable" : ""}${shownLanding == null ? "" : ` land-${shownLanding}`}${movable && move.moving === id ? " moving" : ""}`}
      data-section-id={id ?? undefined}
      data-drop="section"
      onDragOver={(event) => {
        if (event.dataTransfer.types.includes(SECTION_DRAG)) {
          // A section lands beside another named one: not on itself, and not in the unassigned block.
          if (!movable || move.moving === id) return;
          event.preventDefault();
          const bounds = event.currentTarget.getBoundingClientRect();
          setLanding(event.clientY < bounds.top + bounds.height / 2 ? "before" : "after");
          return;
        }
        event.preventDefault();
        setOver(true);
      }}
      onDragLeave={(event) => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
        setOver(false);
        setLanding(null);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setOver(false);
        const section = event.dataTransfer.getData(SECTION_DRAG);
        if (section.length > 0) {
          const position = landing ?? "after";
          setLanding(null);
          if (movable && section !== id) move.onDrop(section, position);
          return;
        }
        const agentId = event.dataTransfer.getData("text/plain");
        if (agentId.length > 0) onDrop(agentId);
      }}
    >
      {name != null ? (
        <div
          className={`section-head${movable && move.htmlDrag ? " draggable" : ""}`}
          {...(onMenu == null ? {} : menu)}
          draggable={movable && move.htmlDrag}
          onDragStart={(event) => {
            if (!movable) return;
            event.dataTransfer.setData(SECTION_DRAG, id);
            event.dataTransfer.effectAllowed = "move";
            move.onStart();
          }}
          onDragEnd={() => move?.onEnd()}
        >
          {onFold != null ? (
            <button
              type="button"
              className={`section-fold${folded ? " folded" : ""}`}
              onClick={onFold}
              aria-expanded={!folded}
              aria-label={folded ? t.expandSection(name) : t.collapseSection(name)}
              title={movable ? t.dragSection(name) : folded ? t.expandSection(name) : t.collapseSection(name)}
              aria-keyshortcuts={movable ? "Alt+ArrowUp Alt+ArrowDown" : undefined}
              onKeyDown={(event) => {
                if (!movable || !event.altKey || event.ctrlKey || event.metaKey || (event.key !== "ArrowUp" && event.key !== "ArrowDown")) return;
                event.preventDefault();
                move.onStep(event.key === "ArrowUp" ? -1 : 1);
              }}
            >
              <svg viewBox="0 0 20 20" width="12" height="12" aria-hidden="true">
                <path d="m5 7.5 5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="section-name">{name}</span>
            </button>
          ) : (
            <span className="section-name">{name}</span>
          )}
          <span className="section-count muted">{count}</span>
          {onMenu != null ? (
            <button type="button" className="icon small section-more" aria-label={t.optionsForSection(name)} onClick={(event) => onMenu(event.clientX, event.clientY)}>
              ···
            </button>
          ) : null}
        </div>
      ) : null}
      {folded ? null : <ul className="agents">{children}</ul>}
      {count === 0 && name != null && !folded ? <div className="section-empty muted">{dragging ? t.dropHere : t.nothingHere}</div> : null}
    </section>
  );
}

/**
 * The roster, as the prototype's sidebar: search on top, the pinned rail,
 * then the sections and whoever is in none, the most recently active first,
 * with hidden bots out of sight until asked for. Rows drag between sections
 * and onto the rail; a right click or a long press opens a row's menu. It
 * renders from the roster stream and the host's sidebar settings and nothing
 * else, so it is the same after a reload and on every device.
 */
export function Sidebar({ agents, sidebar, selectedId, seen, forcedUnread, now, status, onSelect, onCreate, onDelete, onDuplicate, onEditProfile, onMarkUnread, onTogglePinned, onReorderPinned, onMoveToSection, onNewSection, onRenameSection, onRemoveSection, onReorderSection, onToggleHidden, onOpenSettings, onOpenPlugins, onOpenHost, onCollapse, away = false }: Props): React.JSX.Element {
  const t = useText(SIDEBAR);
  const [query, setQuery] = useState("");
  /** Only the bots with something going on: waiting on the user, at work, or unread. */
  const [activity, setActivity] = useState(false);
  const [folded, setFolded] = useState(storedFolded);
  const fold = (id: string): void =>
    setFolded((current) => {
      const next = toggleFolded(current, id);
      try {
        localStorage.setItem(FOLDED_KEY, JSON.stringify([...next]));
      } catch {
        // A private window may refuse storage; the fold still holds until the page reloads.
      }
      return next;
    });
  const [creating, setCreating] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [railOver, setRailOver] = useState(false);
  const [menu, setMenu] = useState<MenuState | null>(null);
  const panel = usePresence(creating, 220);
  useBackClose(creating, () => setCreating(false));
  /**
   * Whether a row is being dragged, as the DOM sees it. The state that draws
   * the drop targets is set a tick later: Chrome abandons a drag whose start
   * handler changes the DOM, and with nothing pinned yet the rail appearing
   * was exactly that change, so dropping only ever worked once one was.
   */
  const dragRef = useRef(false);
  const dragStart = (): void => {
    dragRef.current = true;
    setTimeout(() => {
      if (dragRef.current) setDragging(true);
    }, 0);
  };
  const dragEnd = (): void => {
    dragRef.current = false;
    setDragging(false);
  };
  /*
   * A drag also ends wherever it is dropped. The dragged element's own
   * `dragend` never comes when the drop takes it off the page (the last pin
   * dragged off the rail), and the drop targets, the empty rail's "Drop here
   * to pin" among them, would otherwise stay up.
   */
  useEffect(() => {
    if (!dragging) return;
    const over = (): void => dragEnd();
    document.addEventListener("drop", over);
    document.addEventListener("dragend", over);
    return () => {
      document.removeEventListener("drop", over);
      document.removeEventListener("dragend", over);
    };
  }, [dragging]);

  /** A row lifted by a long press, following the finger; `moved` once it has left where it was pressed. */
  interface Lift {
    readonly agent: RosterAgent;
    x: number;
    y: number;
    moved: boolean;
    readonly startX: number;
    readonly startY: number;
  }
  type TouchTarget = { readonly kind: "rail" } | { readonly kind: "section"; readonly id: string | null } | { readonly kind: "pin"; readonly id: string; readonly position: "before" | "after" };
  const [lift, setLift] = useState<Lift | null>(null);
  const liftRef = useRef<Lift | null>(null);
  const [touchOver, setTouchOver] = useState<TouchTarget | null>(null);

  /** What lies under the finger: a pin (with its side), the rail, or a section; the ghost is not in the way, it takes no pointer. */
  const targetAt = (x: number, y: number): TouchTarget | null => {
    const element = document.elementFromPoint(x, y);
    if (element == null) return null;
    const pin = element.closest<HTMLElement>("[data-pin-id]");
    if (pin?.dataset.pinId != null) {
      const bounds = pin.getBoundingClientRect();
      return { kind: "pin", id: pin.dataset.pinId, position: x < bounds.left + bounds.width / 2 ? "before" : "after" };
    }
    if (element.closest('[data-drop="rail"]') != null) return { kind: "rail" };
    const section = element.closest<HTMLElement>('[data-drop="section"]');
    if (section != null) return { kind: "section", id: section.dataset.sectionId ?? null };
    return null;
  };

  /**
   * The long press has fired: the row is lifted. From here the finger owns
   * the page: moving carries the row, and where it is let go decides; let go
   * without moving, the row's menu opens where it was pressed. The touch
   * events are taken on the document, and not passively, so the list does
   * not scroll and no click follows the release.
   */
  const liftStart = (agent: RosterAgent, x: number, y: number): void => {
    const state: Lift = { agent, x, y, startX: x, startY: y, moved: false };
    liftRef.current = state;
    setLift({ ...state });
    setDragging(true);
    document.body.classList.add("touch-dragging");
    // Nothing else on a phone says the row is now carried rather than merely pressed; a home screen answers with a tap.
    navigator.vibrate?.(12);
    const cleanup = (): void => {
      document.removeEventListener("touchmove", move);
      document.removeEventListener("touchend", end);
      document.removeEventListener("touchcancel", cancel);
      document.body.classList.remove("touch-dragging");
      setLift(null);
      setTouchOver(null);
      dragEnd();
    };
    const move = (event: TouchEvent): void => {
      const touch = event.touches[0];
      const current = liftRef.current;
      if (touch == null || current == null) return;
      event.preventDefault();
      current.x = touch.clientX;
      current.y = touch.clientY;
      if (!current.moved && wandered({ x: current.startX, y: current.startY }, { x: touch.clientX, y: touch.clientY })) current.moved = true;
      setLift({ ...current });
      setTouchOver(current.moved ? targetAt(touch.clientX, touch.clientY) : null);
    };
    const end = (event: TouchEvent): void => {
      const current = liftRef.current;
      liftRef.current = null;
      cleanup();
      if (current == null) return;
      event.preventDefault();
      if (!current.moved) {
        agentMenu(current.agent, current.x, current.y);
        return;
      }
      const target = targetAt(current.x, current.y);
      if (target == null) return;
      if (target.kind === "rail") {
        if (!sidebar.pinned.includes(current.agent.id)) onTogglePinned(current.agent.id);
      } else if (target.kind === "section") {
        onMoveToSection(current.agent.id, target.id);
      } else if (target.id !== current.agent.id) {
        onReorderPinned(current.agent.id, target.id, target.position);
      }
    };
    const cancel = (): void => {
      liftRef.current = null;
      cleanup();
    };
    document.addEventListener("touchmove", move, { passive: false });
    document.addEventListener("touchend", end, { passive: false });
    document.addEventListener("touchcancel", cancel);
  };
  /** A section lifted by a long press on its name, following the finger. */
  interface SectionLift {
    readonly id: string;
    readonly name: string;
    x: number;
    y: number;
    moved: boolean;
    readonly startX: number;
    readonly startY: number;
  }
  const [sectionLift, setSectionLift] = useState<SectionLift | null>(null);
  const [sectionOver, setSectionOver] = useState<{ readonly id: string; readonly position: "before" | "after" } | null>(null);

  /** The named section under the finger, and which half: where the carried one would land. */
  const sectionAt = (x: number, y: number, carried: string): { id: string; position: "before" | "after" } | null => {
    const section = document.elementFromPoint(x, y)?.closest<HTMLElement>("[data-section-id]");
    const id = section?.dataset.sectionId;
    if (section == null || id == null || id === carried) return null;
    const bounds = section.getBoundingClientRect();
    return { id, position: y < bounds.top + bounds.height / 2 ? "before" : "after" };
  };

  /**
   * A section's name held down on a phone: the section lifts, as a row does.
   * Carried, it lands before or after the section it is let go on, a line
   * showing which; let go in place, its menu opens where it was pressed.
   */
  const sectionLiftStart = (id: string, name: string, x: number, y: number): void => {
    let current: SectionLift = { id, name, x, y, startX: x, startY: y, moved: false };
    setSectionLift({ ...current });
    document.body.classList.add("touch-dragging");
    navigator.vibrate?.(12);
    const cleanup = (): void => {
      document.removeEventListener("touchmove", move);
      document.removeEventListener("touchend", end);
      document.removeEventListener("touchcancel", cleanup);
      document.body.classList.remove("touch-dragging");
      setSectionLift(null);
      setSectionOver(null);
      setMovingSection(null);
    };
    const move = (event: TouchEvent): void => {
      const touch = event.touches[0];
      if (touch == null) return;
      event.preventDefault();
      const moved = current.moved || wandered({ x: current.startX, y: current.startY }, { x: touch.clientX, y: touch.clientY });
      if (moved && !current.moved) setMovingSection(id);
      current = { ...current, x: touch.clientX, y: touch.clientY, moved };
      setSectionLift({ ...current });
      setSectionOver(moved ? sectionAt(touch.clientX, touch.clientY, id) : null);
    };
    const end = (event: TouchEvent): void => {
      const last = current;
      cleanup();
      event.preventDefault();
      if (!last.moved) {
        sectionMenu(id, name, last.x, last.y);
        return;
      }
      const target = sectionAt(last.x, last.y, id);
      if (target != null) onReorderSection(id, target.id, target.position);
    };
    document.addEventListener("touchmove", move, { passive: false });
    document.addEventListener("touchend", end, { passive: false });
    document.addEventListener("touchcancel", cleanup);
  };
  const named = new Map(agents.map((agent) => [agent.id, agent.name]));
  // A finger carries a row with the long press above; only a pointer gets the browser's own drag.
  const htmlDrag = !useMedia(TOUCH_QUERY);
  const unreachable = status === "reconnecting" || status === "closed";
  const searching = query.trim().length > 0;
  const arranged = arrange(agents, sidebar);
  // Pins grow in, shrink away and slide along the rail; rows slide to where a new message, a drag or a fold puts them (flip.ts).
  /** The section being dragged by its name. Rows are not dragging meanwhile, so the rail and the rows offer it nothing. */
  const [movingSection, setMovingSection] = useState<string | null>(null);
  const stepSection = (id: string, direction: -1 | 1): void => {
    const step = pinStep(sidebar.sections.map((section) => section.id), id, direction);
    if (step == null) return;
    onReorderSection(id, step.targetId, step.position);
    // The name is drawn anew in its new place; the keyboard stays on it for the next step.
    requestAnimationFrame(() => document.querySelector<HTMLElement>(`[data-section-id="${CSS.escape(id)}"] .section-fold`)?.focus());
  };
  const sectionMove = (id: string, name: string): SectionMove => ({
    htmlDrag,
    onLift: (x, y) => sectionLiftStart(id, name, x, y),
    landing: sectionOver?.id === id ? sectionOver.position : null,
    moving: movingSection,
    // A tick later, as a row's: Chrome drops a drag whose start changes the page.
    onStart: () => setTimeout(() => setMovingSection(id), 0),
    onEnd: () => setMovingSection(null),
    onDrop: (dragged, position) => onReorderSection(dragged, id, position),
    onStep: (direction) => stepSection(id, direction),
  });
  const pinRail = useRef<HTMLDivElement>(null);
  const roster = useRef<HTMLDivElement>(null);
  useFlip(pinRail, "data-pin-id", arranged.pinned.map((agent) => agent.id));
  useFlip(roster, "data-agent-id", undefined, "data-section-id");

  const unreadOf = (agent: RosterAgent): boolean => agent.id !== selectedId && (forcedUnread.has(agent.id) || isUnread(agent, seen[agent.id]));
  const membersOf = (agent: RosterAgent): string => (agent.members.length === 0 ? t.emptyGroup : agent.members.map((id) => named.get(id) ?? "?").join(", "));

  /** A name to ask for, in the app's own dialog (NameDialog.tsx); kept while the dialog plays its way out. */
  const [naming, setNaming] = useState<NameRequest | null>(null);
  const lastNaming = useRef<NameRequest | null>(null);
  if (naming != null) lastNaming.current = naming;
  /** Each ask is a dialog of its own, so one opened while the last is leaving starts with its own text. */
  const asked = useRef(0);
  const askName = (request: NameRequest): void => {
    asked.current += 1;
    setNaming(request);
  };
  const nameDialog = usePresence(naming != null, 220);

  /** The prototype's row menu, item for item. */
  const agentMenu = (agent: RosterAgent, x: number, y: number): void => {
    const pinned = sidebar.pinned.includes(agent.id);
    const hidden = sidebar.hidden.includes(agent.id);
    const current = sectionOf(sidebar, agent.id);
    const items: Array<MenuItem | Divider> = [
      { label: pinned ? t.unpin : t.pin, icon: pinned ? "unpin" : "pin", onSelect: () => onTogglePinned(agent.id) },
      {
        label: t.moveTo,
        icon: "folder",
        children: [
          ...sidebar.sections.map((section): MenuItem => ({ label: section.name, icon: "section", checked: current?.id === section.id, onSelect: () => onMoveToSection(agent.id, section.id) })),
          { label: t.unassigned, icon: "section", checked: current == null && !pinned, onSelect: () => onMoveToSection(agent.id, null) },
          {
            label: t.newSection,
            icon: "plus",
            onSelect: () => askName({ title: t.newSection, note: t.newSectionNote(agent.name), placeholder: t.sectionExamples, action: t.create, onName: (name) => onNewSection(name, agent.id) }),
          },
        ],
      },
      { label: t.markUnread, icon: "unread", onSelect: () => onMarkUnread(agent.id) },
      "divider",
      { label: agent.kind === "group" ? t.editGroup : t.editProfile, icon: "edit", onSelect: () => onEditProfile(agent.id) },
      ...(agent.kind === "group" ? [] : [{ label: t.duplicate, icon: "duplicate", onSelect: () => void onDuplicate(agent) } satisfies MenuItem]),
      "divider",
      { label: t.copyId, icon: "copy", onSelect: () => void navigator.clipboard?.writeText(agent.id) },
      "divider",
      { label: hidden ? t.showInSidebar : t.hideFromSidebar, icon: hidden ? "show" : "hide", onSelect: () => onToggleHidden(agent.id) },
      { label: t.delete, icon: "trash", danger: true, onSelect: () => void onDelete(agent) },
    ];
    setMenu({ x, y, items });
  };

  const sectionMenu = (id: string, name: string, x: number, y: number): void => {
    const ids = sidebar.sections.map((section) => section.id);
    const at = ids.indexOf(id);
    setMenu({
      x,
      y,
      items: [
        ...(at > 0 ? [{ label: t.moveSectionUp, icon: "up", onSelect: () => stepSection(id, -1) } satisfies MenuItem] : []),
        ...(at >= 0 && at < ids.length - 1 ? [{ label: t.moveSectionDown, icon: "down", onSelect: () => stepSection(id, 1) } satisfies MenuItem] : []),
        {
          label: t.renameSection,
          icon: "edit",
          onSelect: () => askName({ title: t.renameSection, initial: name, action: t.rename, onName: (renamed) => onRenameSection(id, renamed) }),
        },
        { label: t.deleteSection, icon: "trash", danger: true, onSelect: () => onRemoveSection(id) },
      ],
    });
  };

  const row = (agent: RosterAgent): React.JSX.Element => (
    <Row key={agent.id} agent={agent} selected={agent.id === selectedId} unread={unreadOf(agent)} now={now} members={membersOf(agent)} onSelect={() => onSelect(agent.id)} onMenu={(x, y) => agentMenu(agent, x, y)} onLift={(x, y) => liftStart(agent, x, y)} lifted={lift?.agent.id === agent.id} htmlDrag={htmlDrag} onDragStart={dragStart} onDragEnd={dragEnd} />
  );

  const matches = searching ? orderRoster(agents).filter((agent) => matchesQuery(agent, query)) : [];
  const active = activity ? activityList(agents, unreadOf) : [];
  const busyCount = agents.filter((agent) => agent.busy || previewOf(agent)?.attention === true).length;
  const anySections = sidebar.sections.length > 0;

  return (
    <aside className={`sidebar${dragging ? " dragging" : ""}`} aria-label={t.bots} inert={away} aria-hidden={away || undefined}>
      <header className="sidebar-head">
        <span className="sidebar-brand">
          <BrandMark />
          {/* A phone's roster is a list with a title of its own; a wide screen names the app. */}
          <span className="brand-wide">ark-bot</span>
          <span className="brand-phone">{t.bots}</span>
        </span>
        <button type="button" className={`icon sidebar-tool activity-toggle${activity ? " on" : ""}`} onClick={() => setActivity((on) => !on)} aria-pressed={activity} aria-label={t.activityLabel} title={t.activityTitle}>
          <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
            <path d="M10 2.5a4.5 4.5 0 0 0-4.5 4.5v3.2L4 13h12l-1.5-2.8V7A4.5 4.5 0 0 0 10 2.5ZM8 15.5a2 2 0 0 0 4 0" fill={activity ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
          </svg>
          {busyCount > 0 && !activity ? <span className="activity-count" aria-hidden="true">{busyCount}</span> : null}
        </button>
        {onCollapse != null ? (
          <button type="button" className="icon sidebar-tool collapse-sidebar" onClick={onCollapse} aria-label={t.hideSidebar} title={t.hideSidebarTitle(shortcutLabel("B"))}>
            <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
              <rect x="2.5" y="3.5" width="15" height="13" rx="2.2" fill="none" stroke="currentColor" strokeWidth="1.6" />
              <path d="M7.5 3.5v13M13.5 8 11.5 10l2 2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        ) : null}
        <button type="button" className="icon new-chat-button" onClick={() => setCreating((open) => !open)} aria-expanded={creating} aria-label={t.newLabel} title={t.newTitle}>
          +
        </button>
      </header>
      <label className="search">
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
          <circle cx="7" cy="7" r="4.5" />
          <path d="M10.5 10.5 14 14" />
        </svg>
        <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t.search} aria-label={t.searchBots} />
      </label>
      {unreachable ? (
        <div className="sidebar-state" role="status">
          <strong>{t.unreachableTitle}</strong>
          <span>{t.unreachableAbout}</span>
          {onOpenHost != null ? (
            <button type="button" className="linkish" onClick={onOpenHost}>
              {t.checkHost}
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="roster" ref={roster}>
        <ActiveGlide selector=".agent-row.selected" className="glide-row" />
        {searching ? (
          <ul className="agents">
            {matches.map(row)}
            {matches.length === 0 ? <li className="hint">{t.nothingMatches(query)}</li> : null}
          </ul>
        ) : activity ? (
          <section className="agents-section activity-section" key="activity">
            <div className="section-head">
              <span className="section-name">{t.activity}</span>
              <span className="section-count muted">{active.length}</span>
            </div>
            <ul className="agents">
              {active.map(row)}
              {active.length === 0 ? <li className="hint">{t.nothingGoingOn}</li> : null}
            </ul>
          </section>
        ) : (
          <>
            {arranged.pinned.length > 0 || dragging ? (
              <div
                ref={pinRail}
                className={`pinned-rail${railOver || touchOver?.kind === "rail" ? " drop-target" : ""}${arranged.pinned.length === 0 ? " empty" : ""}`}
                role="group"
                aria-label={t.pinnedBots}
                data-drop="rail"
                onDragOver={(event) => {
                  event.preventDefault();
                  setRailOver(true);
                }}
                onDragLeave={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setRailOver(false);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  setRailOver(false);
                  const id = event.dataTransfer.getData("text/plain");
                  if (id.length > 0 && !sidebar.pinned.includes(id)) onTogglePinned(id);
                }}
              >
                {arranged.pinned.map((agent) => (
                  <Pin
                    key={agent.id}
                    agent={agent}
                    selected={agent.id === selectedId}
                    unread={unreadOf(agent)}
                    onSelect={() => onSelect(agent.id)}
                    onMenu={(x, y) => agentMenu(agent, x, y)}
                    onLift={(x, y) => liftStart(agent, x, y)}
                    lifted={lift?.agent.id === agent.id}
                    htmlDrag={htmlDrag}
                    touchOver={touchOver?.kind === "pin" && touchOver.id === agent.id ? touchOver.position : null}
                    onDrop={(id, position) => onReorderPinned(id, agent.id, position)}
                    onStep={(direction) => {
                      const step = pinStep(arranged.pinned.map((one) => one.id), agent.id, direction);
                      if (step == null) return;
                      onReorderPinned(agent.id, step.targetId, step.position);
                      // The pin is drawn anew in its new place; the keyboard stays on it for the next step.
                      requestAnimationFrame(() => document.querySelector<HTMLElement>(`[data-pin-id="${CSS.escape(agent.id)}"]`)?.focus());
                    }}
                    onDragStart={dragStart}
                    onDragEnd={dragEnd}
                  />
                ))}
                {arranged.pinned.length === 0 ? <span className="muted rail-hint">{t.dropToPin}</span> : null}
              </div>
            ) : null}
            {arranged.sections.map((section) => (
              <Section key={section.id} id={section.id} name={section.name} count={section.agents.length} dragging={dragging} touchOver={touchOver?.kind === "section" && touchOver.id === section.id} folded={folded.has(section.id) && !dragging} onFold={() => fold(section.id)} onDrop={(agentId) => onMoveToSection(agentId, section.id)} onMenu={(x, y) => sectionMenu(section.id, section.name, x, y)} move={sectionMove(section.id, section.name)}>
                {section.agents.map(row)}
              </Section>
            ))}
            {anySections && arranged.unassigned.length === 0 && !dragging && agents.length > 0 ? null : (
              <Section id={null} name={anySections ? t.unassigned : arranged.unassigned.length > 0 ? t.bots : null} count={arranged.unassigned.length} dragging={dragging} touchOver={touchOver?.kind === "section" && touchOver.id == null} folded={folded.has("") && !dragging && agents.length > 0} onFold={() => fold("")} onDrop={(agentId) => onMoveToSection(agentId, null)}>
                {arranged.unassigned.map(row)}
                {agents.length === 0 && !unreachable ? <li className="hint">{status === "connecting" ? t.connecting : t.noBots}</li> : null}
              </Section>
            )}
            {showHidden && arranged.hidden.length > 0 ? (
              <section className="agents-section hidden-section">
                <div className="section-head">
                  <span className="section-name">{t.hidden}</span>
                  <span className="section-count muted">{arranged.hidden.length}</span>
                </div>
                <ul className="agents">{arranged.hidden.map(row)}</ul>
              </section>
            ) : null}
          </>
        )}
      </div>
      <footer className="sidebar-foot">
        <button type="button" className="foot-link foot-plugins" onClick={onOpenPlugins} title={t.pluginsTitle}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 2v5M15 2v5M6 7h12v4a6 6 0 0 1-12 0V7zM12 17v5" />
          </svg>
          <span>{t.plugins}</span>
        </button>
        {arranged.hidden.length > 0 ? (
          <button type="button" className="foot-link" onClick={() => setShowHidden((open) => !open)} aria-expanded={showHidden}>
            <span>{t.hiddenBots}</span>
            <span className="muted">{arranged.hidden.length}</span>
          </button>
        ) : null}
        {onOpenHost != null ? (
          <button type="button" className="foot-link" onClick={onOpenHost} title={t.hostTitle}>
            <span>{t.host}</span>
            <span className="muted">{hostName()}</span>
          </button>
        ) : null}
        <AccountFoot onOpenSettings={onOpenSettings} />
      </footer>
      {menu != null ? <ContextMenu menu={menu} onClose={() => setMenu(null)} /> : null}
      {/* Over the whole window, not the roster: a style may give the roster a filter, which would hold a fixed dialog inside it. */}
      {nameDialog.mounted && lastNaming.current != null ? createPortal(<NameDialog key={asked.current} request={lastNaming.current} closing={nameDialog.closing} onClose={() => setNaming(null)} />, document.body) : null}
      {lift != null && lift.moved
        ? createPortal(
            <div className="drag-ghost" style={{ left: lift.x, top: lift.y }} aria-hidden="true">
              <Avatar id={lift.agent.id} name={lift.agent.name} avatar={lift.agent.config.avatar} size={28} />
              {lift.agent.name}
            </div>,
            document.body,
          )
        : null}
      {sectionLift != null && sectionLift.moved
        ? createPortal(
            <div className="drag-ghost" style={{ left: sectionLift.x, top: sectionLift.y }} aria-hidden="true">
              <span className="drag-ghost-icon">
                <MenuIcon name="folder" />
              </span>
              {sectionLift.name}
            </div>,
            document.body,
          )
        : null}
      {panel.mounted ? <NewChat agents={agents} onCreate={onCreate} onOpen={onSelect} onClose={() => setCreating(false)} closing={panel.closing} /> : null}
    </aside>
  );
}
